import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import useStore from "../../../helpers/useStore";
import { DoctorContext } from "../../../stores/Doctor/doctor.provider";
import notificationStore from "../../../stores/Notification/notificationStore";

export type MergeEntityType = "doctor" | "clinic";

interface FieldDef {
  key: string;
  label: string;
  scope: "top" | "contactInfo";
  multiline?: boolean;
}

const DOCTOR_FIELDS: FieldDef[] = [
  { key: "LANR", label: "LANR", scope: "top" },
  { key: "salutation", label: "Anrede", scope: "contactInfo" },
  { key: "firstNames", label: "Vorname(n)", scope: "contactInfo" },
  { key: "lastName", label: "Nachname", scope: "contactInfo" },
  { key: "phone1", label: "Telefon", scope: "contactInfo" },
  { key: "mobile", label: "Mobil", scope: "contactInfo" },
  { key: "fax", label: "Fax", scope: "contactInfo" },
  { key: "email", label: "E-Mail", scope: "contactInfo" },
  { key: "addressLine1", label: "Adresse", scope: "contactInfo" },
  { key: "city", label: "Ort", scope: "contactInfo" },
  { key: "zip", label: "PLZ", scope: "contactInfo" },
];

const CLINIC_FIELDS: FieldDef[] = [
  { key: "BSNR", label: "BSNR", scope: "top" },
  { key: "businessName", label: "Praxisname", scope: "contactInfo" },
  { key: "phone1", label: "Telefon", scope: "contactInfo" },
  { key: "fax", label: "Fax", scope: "contactInfo" },
  { key: "email", label: "E-Mail", scope: "contactInfo" },
  { key: "addressLine1", label: "Adresse", scope: "contactInfo" },
  { key: "addressLine2", label: "Adresszusatz", scope: "contactInfo" },
  { key: "city", label: "Ort", scope: "contactInfo" },
  { key: "zip", label: "PLZ", scope: "contactInfo" },
  { key: "notes", label: "Hinweise", scope: "top", multiline: true },
];

const STEPS = ["Duplikat wählen", "Daten prüfen", "Bestätigen"];

type AnyEntity = Record<string, any>;

function fieldsFor(type: MergeEntityType): FieldDef[] {
  return type === "doctor" ? DOCTOR_FIELDS : CLINIC_FIELDS;
}

function readField(entity: AnyEntity | null, def: FieldDef): string {
  if (!entity) return "";
  const raw = def.scope === "top" ? entity[def.key] : entity.contactInfo?.[def.key];
  return raw == null ? "" : String(raw);
}

function entityLabel(type: MergeEntityType, entity: AnyEntity | null | undefined): string {
  if (!entity) return "";
  const c = entity.contactInfo || {};
  if (type === "doctor") {
    const name = [c.salutation, c.firstNames, c.lastName].filter(Boolean).join(" ").trim();
    return name || entity.LANR || entity.id || "";
  }
  return c.businessName || entity.BSNR || entity.id || "";
}

function entityIdOnly(type: MergeEntityType, entity: AnyEntity | null | undefined): string {
  if (!entity) return "";
  if (type === "doctor") return entity.LANR ? `LANR ${entity.LANR}` : `ID ${entity.id}`;
  return entity.BSNR ? `BSNR ${entity.BSNR}` : `ID ${entity.id}`;
}

function entitySubLabel(type: MergeEntityType, entity: AnyEntity | null | undefined): string {
  if (!entity) return "";
  const c = entity.contactInfo || {};
  return [entityIdOnly(type, entity), c.city].filter(Boolean).join(" · ");
}

interface MergeEntityDialogProps {
  open: boolean;
  onClose: () => void;
  entityType: MergeEntityType;
  /** The record that is kept (merged INTO). */
  targetId: string;
  onMerged?: () => void;
}

/**
 * Wizard to merge a duplicate doctor/clinic (source) into the current record
 * (target). Step 1 pick the duplicate, step 2 review the merged field values,
 * step 3 confirm — showing how many prescriptions will be moved. The source is
 * deleted; no prescription is ever lost.
 */
const MergeEntityDialog: React.FC<MergeEntityDialogProps> = ({
  open,
  onClose,
  entityType,
  targetId,
  onMerged,
}) => {
  const store = useStore(DoctorContext);
  const fields = fieldsFor(entityType);
  const entityWord = entityType === "doctor" ? "Arzt" : "Praxis";

  const [activeStep, setActiveStep] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<AnyEntity[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [source, setSource] = useState<AnyEntity | null>(null);

  const [targetFull, setTargetFull] = useState<AnyEntity | null>(null);
  const [sourceFull, setSourceFull] = useState<AnyEntity | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [loadingDetails, setLoadingDetails] = useState(false);

  const [affectedRx, setAffectedRx] = useState<number | null>(null);
  const [loadingCount, setLoadingCount] = useState(false);
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const resetState = useCallback(() => {
    setActiveStep(0);
    setSearchTerm("");
    setSearchResults([]);
    setSource(null);
    setTargetFull(null);
    setSourceFull(null);
    setValues({});
    setAffectedRx(null);
    setConfirmChecked(false);
    setSubmitting(false);
  }, []);

  useEffect(() => {
    if (open) resetState();
  }, [open, resetState]);

  // Load the target's details as soon as the dialog opens, so the user always
  // sees which record is being KEPT (the one everything is merged into).
  useEffect(() => {
    if (!open || !targetId) return;
    let cancelled = false;
    (async () => {
      try {
        const t =
          entityType === "doctor"
            ? await store.getDoctorById(targetId)
            : await store.getClinicById(targetId);
        if (!cancelled) setTargetFull(t as AnyEntity);
      } catch {
        /* banner simply won't show */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, targetId, entityType, store]);

  // ---- Step 1: search --------------------------------------------------
  const runSearch = useCallback(
    async (term: string) => {
      if (!term.trim()) {
        setSearchResults([]);
        return;
      }
      setSearchLoading(true);
      try {
        const res =
          entityType === "doctor"
            ? await store.searchDoctor(1, 20, { searchTerm: term })
            : await store.searchClinic(1, 20, { searchTerm: term });
        const items: AnyEntity[] = Array.isArray(res) ? res : res?.data ?? [];
        setSearchResults(items.filter((it) => it?.id && it.id !== targetId));
      } catch (e) {
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    },
    [entityType, store, targetId]
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const term = e.target.value;
    setSearchTerm(term);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(term), 300);
  };

  // ---- Step 2: load full records + prefill -----------------------------
  const loadDetailsAndAdvance = async () => {
    if (!source) return;
    setLoadingDetails(true);
    try {
      const [t, s] = await Promise.all([
        entityType === "doctor" ? store.getDoctorById(targetId) : store.getClinicById(targetId),
        entityType === "doctor" ? store.getDoctorById(source.id) : store.getClinicById(source.id),
      ]);
      setTargetFull(t as AnyEntity);
      setSourceFull(s as AnyEntity);
      const prefill: Record<string, string> = {};
      for (const def of fields) prefill[def.key] = readField(t as AnyEntity, def);
      setValues(prefill);
      setActiveStep(1);
    } catch (e) {
      notificationStore.showMessage("Daten konnten nicht geladen werden.", "error");
    } finally {
      setLoadingDetails(false);
    }
  };

  // ---- Step 3: affected Rx count ---------------------------------------
  const loadCountAndAdvance = async () => {
    if (!source) return;
    setLoadingCount(true);
    try {
      const count =
        entityType === "doctor"
          ? await store.countAffectedRxsForDoctor(source.id)
          : await store.countAffectedRxsForClinic(source.id);
      setAffectedRx(count);
    } catch (e) {
      setAffectedRx(null);
    } finally {
      setLoadingCount(false);
      setActiveStep(2);
    }
  };

  // ---- Submit ----------------------------------------------------------
  const handleMerge = async () => {
    if (!source) return;
    const contactInfo: Record<string, string> = {};
    const top: Record<string, string> = {};
    for (const def of fields) {
      const v = (values[def.key] ?? "").trim();
      if (def.scope === "contactInfo") contactInfo[def.key] = v;
      else top[def.key] = v;
    }
    const updatedTarget =
      entityType === "doctor"
        ? { LANR: top.LANR || undefined, contactInfo }
        : { BSNR: top.BSNR || undefined, notes: top.notes ?? "", contactInfo };

    setSubmitting(true);
    try {
      if (entityType === "doctor") {
        await store.mergeDoctors({ targetId, sourceId: source.id, updatedTarget });
      } else {
        await store.mergeClinics({ targetId, sourceId: source.id, updatedTarget });
      }
      notificationStore.showMessage(`${entityWord} erfolgreich zusammengeführt.`, "success");
      onMerged?.();
      onClose();
    } catch (error: any) {
      notificationStore.showMessage(
        error?.response?.data?.message || `Zusammenführen fehlgeschlagen.`,
        "error",
        error
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleNext = () => {
    if (activeStep === 0) loadDetailsAndAdvance();
    else if (activeStep === 1) loadCountAndAdvance();
    else handleMerge();
  };

  const handleBack = () => setActiveStep((s) => Math.max(0, s - 1));

  const nextDisabled =
    (activeStep === 0 && (!source || loadingDetails)) ||
    (activeStep === 1 && loadingCount) ||
    (activeStep === 2 && (!confirmChecked || submitting));

  const nextLabel = activeStep === 2 ? "Zusammenführen" : "Weiter";

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span>Duplikate zusammenführen ({entityWord})</span>
        <IconButton size="small" onClick={onClose} disabled={submitting}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* STEP 1 — pick the duplicate */}
        {activeStep === 0 && (
          <Box>
            <Alert severity="success" icon={false} sx={{ mb: 2 }}>
              <Typography
                variant="caption"
                sx={{ display: "block", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}
              >
                Dieser Eintrag bleibt bestehen (Ziel)
              </Typography>
              <Typography variant="body2">
                <strong>{entityLabel(entityType, targetFull) || "…"}</strong>
                {targetFull ? ` · ${entitySubLabel(entityType, targetFull)}` : ""}
              </Typography>
            </Alert>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Wähle das <strong>Duplikat</strong>, das mit diesem Eintrag zusammengeführt wird. Das
              ausgewählte Duplikat wird danach <strong>gelöscht</strong> — seine Rezepte, Tickets und
              Zuordnungen wandern hierher.
            </Typography>
            <TextField
              autoFocus
              fullWidth
              size="small"
              placeholder={entityType === "doctor" ? "Arzt suchen (Name, LANR)…" : "Praxis suchen (Name, BSNR)…"}
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <Box sx={{ mt: 1, minHeight: 180, maxHeight: 300, overflowY: "auto" }}>
              {searchLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : searchResults.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: "center" }}>
                  {searchTerm.trim() ? "Keine Treffer." : "Tippe, um zu suchen."}
                </Typography>
              ) : (
                <List dense disablePadding>
                  {searchResults.map((item) => (
                    <ListItemButton
                      key={item.id}
                      selected={source?.id === item.id}
                      onClick={() => setSource(item)}
                    >
                      <ListItemText
                        primary={entityLabel(entityType, item)}
                        secondary={entitySubLabel(entityType, item)}
                      />
                    </ListItemButton>
                  ))}
                </List>
              )}
            </Box>
          </Box>
        )}

        {/* STEP 2 — review merged fields */}
        {activeStep === 1 && (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>{entityLabel(entityType, sourceFull) || entityLabel(entityType, source)}</strong>{" "}
              ({entityIdOnly(entityType, sourceFull || source)}) wird in{" "}
              <strong>{entityLabel(entityType, targetFull)}</strong> ({entityIdOnly(entityType, targetFull)}){" "}
              zusammengeführt. Prüfe die beizubehaltenden Werte.
            </Alert>
            {fields.map((def) => {
              const sourceVal = readField(sourceFull, def);
              const current = values[def.key] ?? "";
              const showSource = sourceVal && sourceVal !== current;
              return (
                <Box key={def.key} sx={{ mb: 1.5 }}>
                  <TextField
                    label={def.label}
                    fullWidth
                    size="small"
                    multiline={def.multiline}
                    minRows={def.multiline ? 2 : undefined}
                    value={current}
                    onChange={(e) =>
                      setValues((prev) => ({ ...prev, [def.key]: e.target.value }))
                    }
                  />
                  {showSource && (
                    <Box sx={{ mt: 0.5 }}>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`Quelle übernehmen: ${sourceVal}`}
                        onClick={() =>
                          setValues((prev) => ({ ...prev, [def.key]: sourceVal }))
                        }
                      />
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        )}

        {/* STEP 3 — confirm */}
        {activeStep === 2 && (
          <Box>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Der Eintrag <strong>{entityLabel(entityType, sourceFull)}</strong>{" "}
              ({entityIdOnly(entityType, sourceFull)}) wird <strong>gelöscht</strong>. Alle
              verknüpften Rezepte werden auf <strong>{entityLabel(entityType, targetFull)}</strong>{" "}
              ({entityIdOnly(entityType, targetFull)}) verschoben — es geht kein Rezept verloren.
            </Alert>
            <Box
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                p: 2,
                mb: 2,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {loadingCount ? "…" : affectedRx ?? "—"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Rezepte werden auf den beibehaltenen Eintrag verschoben.
              </Typography>
            </Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={confirmChecked}
                  onChange={(e) => setConfirmChecked(e.target.checked)}
                />
              }
              label="Ich bestätige das Zusammenführen und Löschen des Duplikats."
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={submitting} color="inherit">
          Abbrechen
        </Button>
        <Box sx={{ flex: 1 }} />
        {activeStep > 0 && (
          <Button onClick={handleBack} disabled={submitting}>
            Zurück
          </Button>
        )}
        <Button
          variant="contained"
          onClick={handleNext}
          disabled={nextDisabled}
          startIcon={
            loadingDetails || submitting ? <CircularProgress size={16} color="inherit" /> : undefined
          }
        >
          {nextLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MergeEntityDialog;
